import {Action, openDeleteDialog, showNotification} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNil} from 'ramda';

import {useRemoveMinipurchaseOfferedVehicleMutation} from '@dms/api/finance';
import {FullVehicleResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {
  handleApiError,
  useInterestEditPermissions,
  usePricePermissions,
  VehicleWidget,
} from '@dms/shared';

import {buildArray, composePath, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useInterestData} from '../../../../hooks/useInterestData';

interface OfferedVehicleProps extends RequiredTestIdProps {
  vehicle: FullVehicleResponseBody;
  interestId: string;
}

export function OfferedVehicle(props: OfferedVehicleProps) {
  const formatPrice = useFormatCurrency();

  const {canViewAnyOfVehiclePriceFields: canReadPrices} = usePricePermissions({
    vehicleRecordId: null,
    businessCaseRecordId: null,
  });

  const {interest, miniPurchase} = useInterestData(props.interestId);
  const {hasRemoveMinipurchaseOfferedVehicleInterestPermission} = useInterestEditPermissions({
    interestId: props.interestId,
  });

  const [removeMinipurchaseOfferedVehicle, {isLoading: isRemoveMinipurchaseOfferedVehicleLoading}] =
    useRemoveMinipurchaseOfferedVehicleMutation();

  const onRemoveVehicle = (vehicleId: string) => {
    if (!miniPurchase) {
      showNotification.error('missing miniPurchase object');
      return;
    }

    removeMinipurchaseOfferedVehicle({
      minipurchaseId: miniPurchase.id,
      vehicleId,
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
      })
      .catch(handleApiError);
  };

  const mappedVehicle = {
    id: props.vehicle.id,
    vin: props.vehicle.vin,
    licencePlate: props.vehicle.vehicle.registrationPlate,
    firstRegistration: props.vehicle.vehicle.firstRegistrationOn,
    manufacturedOn: props.vehicle.vehicle.manufacturedOn,
    make: props.vehicle.make,
    vehicleType: props.vehicle.type?.toString(),
    modelFamily: props.vehicle.modelFamily,
    trim: props.vehicle.trim,
    title: props.vehicle.title,
    variant: props.vehicle.variant,
    mileage: props.vehicle.vehicle.mileage,
    transmission: props.vehicle.transmission,
    fuelType: props.vehicle.fuelType,
    bodyStyle: props.vehicle.bodyStyle,
    driveType: props.vehicle.drive,
    photos: props.vehicle.photos,
    firstParameter:
      // TODO: this ACL condition is not tied to recordId, should we abstract it?
      canReadPrices && props.vehicle.sellingPrice.withoutVat
        ? formatPrice(
            Number(props.vehicle.sellingPrice.withoutVat.amount),
            props.vehicle.sellingPrice.withoutVat.currency,
            0
          )
        : null,
    secondParameter:
      canReadPrices && props.vehicle.sellingPrice.withVat
        ? formatPrice(
            Number(props.vehicle.sellingPrice.withVat.amount),
            props.vehicle.sellingPrice.withVat.currency,
            0
          )
        : null,
    actions: buildArray<Action>()
      .add({
        type: 'button',
        leftIcon: 'action/delete',
        variant: 'outlined',
        title: i18n.t('general.labels.delete'),
        isDisabled:
          isNil(miniPurchase) ||
          isRemoveMinipurchaseOfferedVehicleLoading ||
          interest?.state === 'UNSUCCESSFUL' ||
          interest?.state === 'CLOSED' ||
          !hasRemoveMinipurchaseOfferedVehicleInterestPermission,
        isLoading: isRemoveMinipurchaseOfferedVehicleLoading,
        onClick: () =>
          openDeleteDialog({
            onConfirm: () => onRemoveVehicle(props.vehicle.id),
          }),
      })
      .add({
        type: 'button',
        title: i18n.t('entity.vehicle.labels.detail'),
        variant: 'outlined',
        leftIcon: 'action/launch',
        onClick: () => {
          window.open(
            composePath(vehiclesRoutes.detail, {params: {id: props.vehicle.id}}),
            '_blank'
          );
        },
      }),
  };

  return (
    <VStack spacing={2}>
      <HStack width="100%" justify="space-between" align="flex-end">
        <VehicleWidget
          isBoxed
          data-testid={suffixTestId(`vehicleWidget-[${props.vehicle.id}]`, props)}
          vehicle={mappedVehicle}
          currentInterestState={interest?.state}
        />
      </HStack>
    </VStack>
  );
}
